import {
	Box,
	CircularProgress,
	Chip,
	Divider,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@mui/material"
import { useTranslation } from "react-i18next"
import { RefObject, useEffect, useRef, useState } from "react"
import { BatchUpdateInterfaceProps } from "./BatchUpdate"
import {
	BatchUpdateInterfaceFieldsFragment,
	GetCurrentUpdateStatusListDocument,
	InterfaceFirmwareVersion,
	UpdateDevice,
	UpdateProgress,
	useGetCurrentUpdateStatusListQuery,
	useUpdateInterfaceFirmwareMutation,
} from "generated/graphql"
import ProgressStatus, { Status } from "components/ProgressStatus"
import CheckIcon from "@mui/icons-material/Check"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import { useMessage } from "providers/MessageProvider"
import { useConfirm } from "providers/ConfirmProvider"
import InterfaceUpdateSelector from "components/InterfaceUpdateSelector"
import { isUpdateStuck } from "tools/isUpdateStuck"
import UpdatingDeviceAlert from "components/UpdatingDeviceAlert"
import { ApolloError } from "@apollo/client"

interface ErrorsObjectsProps {
	interfaceId: string
	errorMessage: string
}

interface Props {
	dialogOpenedAt: Date | null | undefined
	interfaces: BatchUpdateInterfaceFieldsFragment[]
	loadingUpdate: boolean
	updateErrors: ErrorsObjectsProps[]
	version: InterfaceFirmwareVersion | undefined
}

function TableUpdateResults({ dialogOpenedAt, interfaces, loadingUpdate, updateErrors, version }: Props) {
	const { t } = useTranslation(["general", "pvtHeatPumpAdminPage", "heatPumpErrorCard", "heatPumpSettingsPage"])

	const getStatusChip = (iface: BatchUpdateInterfaceFieldsFragment) => {
		if (updateErrors.some((err) => err.interfaceId === iface.id)) {
			return (
				<Chip
					label={updateErrors.find((err) => err.interfaceId === iface.id)?.errorMessage}
					color="error"
					variant="outlined"
					sx={{ pl: 1 }}
					icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
				/>
			)
		}

		if (iface.updateStatus?.progress && iface.updateStatus?.device === UpdateDevice.Interface) {
			switch (iface.updateStatus.progress) {
				case UpdateProgress.Completed:
					if (dialogOpenedAt && new Date(iface.updateStatus.time).getTime() > dialogOpenedAt.getTime()) {
						return (
							<Chip
								label={t("UpdatedToFirmwareVersion", {
									firmwareVersion: iface.firmwareVersion.version,
								})}
								color="info"
								variant="outlined"
								sx={{ pl: 1 }}
								icon={<CheckIcon sx={{ fontSize: 16 }} />}
							/>
						)
					}
					break
				case UpdateProgress.InProgress:
				case UpdateProgress.Requested:
					return (
						<ProgressStatus
							noLogo
							percentage={iface.updateStatus.percentage ?? undefined}
							text={iface.updateStatus.message}
							status={Status.InProgress}
						/>
					)
				case UpdateProgress.Failed:
					return (
						<ProgressStatus
							noLogo
							percentage={iface.updateStatus.percentage ?? undefined}
							text={iface.updateStatus.message}
							status={Status.Error}
						/>
					)
				default:
					return (
						<ProgressStatus
							noLogo
							percentage={iface.updateStatus.percentage ?? undefined}
							text={iface.updateStatus.message}
							status={Status.Error}
						/>
					)
			}
		}

		if (new Date(iface?.firmwareVersion.timestamp).getTime() === new Date(version?.timestamp ?? 0).getTime()) {
			return (
				<Chip
					label={t("CurrentVersion")}
					color="info"
					variant="outlined"
					sx={{ pl: 1 }}
					icon={<CheckIcon sx={{ fontSize: 16 }} />}
				/>
			)
		}

		if (new Date(iface?.firmwareVersion.timestamp).getTime() > new Date(version?.timestamp ?? 0).getTime()) {
			return (
				<Chip
					label={t("SelectedVersionIsOlder")}
					color="warning"
					variant="outlined"
					sx={{ pl: 1 }}
					icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
				/>
			)
		}

		return (
			<Chip
				label={t("UpdateAvailable")}
				color="success"
				variant="outlined"
				sx={{ pl: 1 }}
				icon={<CheckIcon sx={{ fontSize: 16 }} />}
			/>
		)
	}

	return interfaces?.map((iface) => {
		const inProgress =
			(iface.updateStatus?.progress === UpdateProgress.Requested ||
				iface?.updateStatus?.progress === UpdateProgress.InProgress) &&
			iface.updateStatus.progress &&
			iface.updateStatus.device === UpdateDevice.Interface

		return (
			<TableRow key={iface.name}>
				<TableCell>{iface.id}</TableCell>
				<TableCell>{iface.name}</TableCell>
				<TableCell>{iface.firmwareVersion.version}</TableCell>
				<TableCell
					align="right"
					sx={{
						maxWidth: "250px",
						...(inProgress && { p: 0 }), // Remove padding from table when in progress to save spacing
					}}
				>
					{loadingUpdate ? <CircularProgress size={30} /> : getStatusChip(iface)}
				</TableCell>
			</TableRow>
		)
	})
}

export default function BatchUpdateInterface({
	dialogOpenedAt,
	interfaces,
	loading,
	handleRefetch,
}: BatchUpdateInterfaceProps) {
	const { t } = useTranslation(["general", "pvtHeatPumpAdminPage", "heatPumpErrorCard", "heatPumpSettingsPage"])
	const message = useMessage()
	const confirm = useConfirm()

	const [version, setVersion] = useState<InterfaceFirmwareVersion>()
	const [polling, setPolling] = useState(false)
	const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false)
	const [updateErrors, setUpdateErrors] = useState<ErrorsObjectsProps[]>([])

	const resultsRef: RefObject<HTMLElement | null | undefined> = useRef()

	const onlineInterfaceIds = interfaces.filter((item) => item.isOnline).map((item) => item.id)

	const handleGetFirmwareVersion = (interfaceVersion: InterfaceFirmwareVersion) => {
		if (!interfaceVersion) return
		setVersion(interfaceVersion)
	}

	const { data: currentStatus, loading: currentStatusLoading } = useGetCurrentUpdateStatusListQuery({
		variables: {
			interfaceIds: onlineInterfaceIds,
		},
		errorPolicy: "all", // Return data even if some interfaces has errors
		pollInterval: polling ? 1000 : 0,
	})

	useEffect(() => {
		if (!currentStatus?.interfaces) return

		if (!currentStatusLoading) {
			setTimeout(() => {
				setLoadingUpdate(false)
			}, 1000) // For some reason the query currentStatus takes some seconds to load after currentStatusLoading at the interface update
		}

		setPolling(
			currentStatus.interfaces.some(
				(interfaceObj) =>
					interfaceObj?.updateStatus?.progress === UpdateProgress.InProgress ||
					interfaceObj?.updateStatus?.progress === UpdateProgress.Requested,
			),
		)

		currentStatus.interfaces.some((interfaceObj) => {
			if (interfaceObj?.updateStatus?.progress === UpdateProgress.Completed) {
				handleRefetch && handleRefetch()
			}
		})
	}, [currentStatus])

	const [updateInterface] = useUpdateInterfaceFirmwareMutation({
		refetchQueries: [
			{
				query: GetCurrentUpdateStatusListDocument,
				variables: { interfaceIds: onlineInterfaceIds },
				errorPolicy: "all",
			},
		],
	})

	const handleUpdateInterface = async (update: InterfaceFirmwareVersion) => {
		await confirm(
			t("pvtHeatPumpAdminPage:MsgConfirmAllInterfaceUpdate", {
				firmwareVersionName: update.version,
			}),
		)

		// Filter out interfaces/heatpump when in progress or same version
		const availableInterfaces = interfaces
			.filter((iface) => {
				const firmwareVersion = new Date(iface?.firmwareVersion.timestamp).getTime()
				const targetVersion = (version?.timestamp && new Date(version.timestamp).getTime()) ?? 0

				return (
					(iface.isOnline &&
						firmwareVersion !== targetVersion &&
						iface.updateStatus?.progress !== UpdateProgress.InProgress) ||
					isUpdateStuck(iface)
				)
			})
			.map((item) => item.id)

		if (!availableInterfaces?.length) {
			message.info(t("AllSelectedInterfacesBeingUpdated"))
			return
		}

		setPolling(true)
		setLoadingUpdate(true)
		resultsRef?.current?.scrollIntoView({ behavior: "smooth" })

		try {
			await updateInterface({
				variables: { interfaceIds: availableInterfaces, firmwareId: update.id },
			})

			message.success(t("pvtHeatPumpAdminPage:InterfaceUpdateStarted"))
		} catch (e) {
			if (e instanceof ApolloError) {
				const message = e.message
				const errorsObject: ErrorsObjectsProps[] = []

				// If the backend errors are same amount of the availableInterfaces from front-end then set the polling back to false
				if (e.graphQLErrors.length === availableInterfaces?.length) {
					setPolling(false)
					setLoadingUpdate(false)
				}

				e.graphQLErrors.map((error) => {
					const interfaceId = error?.extensions?.interfaceID

					if (typeof interfaceId === "string" && message && interfaceId) {
						errorsObject.push({
							interfaceId: interfaceId,
							errorMessage: (() => {
								switch (true) {
									case message.includes("The operation failed because the requested device isn't online"):
										return t("heatPumpErrorCard:InterfaceNotOnline.Title")

									case message.includes("error updating interface firmware"):
										return t("heatPumpErrorCard:ErrorUpdatingInterfaceFirmware")

									case message.includes("error setting update status of interface"):
										return t("heatPumpErrorCard:ErrorUpdatingInterfaceFirmware")

									default:
										return message
								}
							})(),
						})

						setUpdateErrors(errorsObject)
					}
				})
			}
		}
	}

	return (
		<>
			<UpdatingDeviceAlert currentStatus={currentStatus} />

			<InterfaceUpdateSelector
				handleUpdateInterface={handleUpdateInterface}
				handleGetFirmwareVersion={handleGetFirmwareVersion}
			/>

			<Divider sx={{ mt: 4 }} />
			{loading ? (
				<Stack direction="row" pt={4} justifyContent="center">
					<CircularProgress size={30} />
				</Stack>
			) : (
				<Box ref={resultsRef}>
					<TableContainer>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>{t("InterfaceID")}</TableCell>
									<TableCell>{t("Name")}</TableCell>
									<TableCell>{t("Version")}</TableCell>
									<TableCell align="right">{t("Status")}</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								<TableUpdateResults
									dialogOpenedAt={dialogOpenedAt}
									interfaces={interfaces}
									version={version}
									loadingUpdate={loadingUpdate}
									updateErrors={updateErrors}
								/>
							</TableBody>
						</Table>
					</TableContainer>
				</Box>
			)}
		</>
	)
}
